import { useRef } from 'react';
import { LayoutHeader as Header } from '@/layouts';
import { useTheme } from '@/contexts/ThemeContext';
import { Footer } from '@/components/Footer';
import { ServiceModal } from '@/components/ServiceModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAppData } from '@/contexts/AppDataContext';
import { openWhatsApp } from '@/lib/whatsapp';
import { useStore } from '@/contexts/StoreContext';
import {
  Car,
  CreditCard,
  Shield,
  FileText,
  Video,
  ShoppingCart,
  MessageCircle,
  CheckCircle,
  Wrench,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Search,
  Users,
  Package,
  Settings,
  Gauge,
  Handshake,
  HandCoins
} from 'lucide-react';

// Import service images
import videoCall from '@/assets/services/videoCall.png';
import valuation from '@/assets/services/valuation.png';
import consignment from '@/assets/services/consignment.png';
import consortium from '@/assets/services/consortium.png';
import forwardingAgent from '@/assets/services/forwardingAgent.png';
import financing from '@/assets/services/financing.png';
import partsAccessories from '@/assets/services/partsAccessories.png';
import scheduleReview from '@/assets/services/scheduleReview.png';
import insurance from '@/assets/services/insurance.png';
import scheduleServices from '@/assets/services/scheduleServices.png';
import fipeTable from '@/assets/services/fipeTable.png';
import testdrive from '@/assets/services/testdrive.png';
import directSales from '@/assets/services/directSales.png';

const Services = () => {
  const { config } = useAppData();
  const { selectedStore } = useStore();
  const store = selectedStore || config.stores[0];
  const services = store.services || [];
  const { colors } = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const titleColor = colors.title;
  const textColor = colors.text;

  // Helper function to get service image (from config or default)
  const getServiceImage = (service: any, defaultImage: string) => {
    return service.image || defaultImage;
  };

  // Scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -340, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 340, behavior: 'smooth' });
    }
  };

  // Map service IDs to icons, descriptions and images (default)
  const serviceInfo: Record<string, { icon: any; title: string; description: string; color: string; image: string }> = {
    '65da42048eca108d87c91da6': {
      icon: Video,
      title: 'Agendar Videochamada',
      description: 'Agende uma videochamada para conhecer nossos veículos sem sair de casa',
      color: 'bg-blue-500',
      image: videoCall
    },
    '65dcb7548eca108d87c91dbb': {
      icon: Search,
      title: 'Avaliação',
      description: 'Avaliação profissional do seu veículo com preço justo de mercado',
      color: 'bg-green-500',
      image: valuation
    },
    '65dcb7c58eca108d87c91dbd': {
      icon: HandCoins,
      title: 'Consignação',
      description: 'Deixe seu veículo em consignação conosco e venda com segurança',
      color: 'bg-purple-500',
      image: consignment
    },
    '65dcef468eca108d87c91dc8': {
      icon: Users,
      title: 'Consórcio',
      description: 'Adquira seu veículo através de consórcio com as melhores condições',
      color: 'bg-teal-500',
      image: consortium
    },
    '65dcb6528eca108d87c91db9': {
      icon: FileText,
      title: 'Despachante',
      description: 'Serviços de despachante para documentação veicular completa',
      color: 'bg-orange-500',
      image: forwardingAgent
    },
    '65dcb7898eca108d87c91dbc': {
      icon: CreditCard,
      title: 'Financiamento',
      description: 'Análise de crédito e financiamento com as melhores taxas do mercado',
      color: 'bg-blue-600',
      image: financing
    },
    '6628fd535bda2ae51f6fc5ff': {
      icon: Package,
      title: 'Peças e Acessórios',
      description: 'Peças originais e acessórios para seu veículo',
      color: 'bg-gray-600',
      image: partsAccessories
    },
    '65dcef098eca108d87c91dc7': {
      icon: Wrench,
      title: 'Revisões',
      description: 'Revisões preventivas e corretivas com equipe especializada',
      color: 'bg-slate-600',
      image: scheduleReview
    },
    '65dcb7238eca108d87c91dba': {
      icon: Shield,
      title: 'Seguros',
      description: 'Cotação de seguros com as principais seguradoras do país',
      color: 'bg-red-500',
      image: insurance
    },
    '65dceeac8eca108d87c91dc6': {
      icon: Settings,
      title: 'Serviços',
      description: 'Serviços automotivos especializados para seu veículo',
      color: 'bg-indigo-600',
      image: scheduleServices
    },
    '65dcb7fe8eca108d87c91dbe': {
      icon: TrendingUp,
      title: 'Tabela FIPE',
      description: 'Consulte o valor do seu veículo pela tabela FIPE',
      color: 'bg-cyan-500',
      image: fipeTable
    },
    '65dcb5e78eca108d87c91db8': {
      icon: Gauge,
      title: 'Test-Drive',
      description: 'Agende um test drive e experimente o veículo dos seus sonhos',
      color: 'bg-indigo-500',
      image: testdrive
    },
    '65dcb8548eca108d87c91dbf': {
      icon: Handshake,
      title: 'Vendas Diretas',
      description: 'Vendas diretas com as melhores condições e atendimento personalizado',
      color: 'bg-green-600',
      image: directSales
    }
  };

  const getServiceInfo = (serviceId: string) => {
    return serviceInfo[serviceId] || {
      icon: MessageCircle,
      title: 'Serviço',
      description: 'Entre em contato para mais informações sobre este serviço',
      color: 'bg-gray-500',
      image: ''
    };
  };

  const getServiceButtonText = (serviceId: string) => {
    const buttonTexts: Record<string, string> = {
      // Agendar
      '65da42048eca108d87c91da6': 'Agendar', // Agendar Videochamada
      '65dceeac8eca108d87c91dc6': 'Agendar', // Serviços
      '65dcef098eca108d87c91dc7': 'Agendar', // Revisões
      '65dcb5e78eca108d87c91db8': 'Agendar', // Test-Drive

      // Simular
      '65dcb7898eca108d87c91dbc': 'Simular', // Financiamento

      // Avaliar
      '65dcb7548eca108d87c91dbb': 'Avaliar', // Avaliação
      '65dcb7c58eca108d87c91dbd': 'Avaliar', // Consignação

      // Cotar
      '65dcb7238eca108d87c91dba': 'Cotar', // Seguros
      '65dcb6528eca108d87c91db9': 'Cotar', // Despachante
      '6628fd535bda2ae51f6fc5ff': 'Cotar', // Peças e Acessórios

      // Solicitar
      '65dcef468eca108d87c91dc8': 'Solicitar', // Consórcio
      '65dcb8548eca108d87c91dbf': 'Solicitar', // Vendas Diretas

      // Consultar
      '65dcb7fe8eca108d87c91dbe': 'Consultar', // Tabela FIPE
    };

    return buttonTexts[serviceId] || 'Solicitar Serviço';
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <Header config={config} />

      {/* Hero Section */}
      <section className="py-20 mt-[120px] pb-10">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-bold" style={{ color: titleColor }}>
              Nossos Serviços
            </h1>
            <p className="text-lg" style={{ color: textColor }}>
              Oferecemos uma gama completa de serviços automotivos para atender todas as suas necessidades
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-10 pt-0">
        <div className="container mx-auto px-4">

          {/* Title with Navigation */}
          <div className="flex items-center justify-end mb-8">

            {/* Desktop Navigation Buttons - Only show if 5+ services */}
            {services.length >= 5 && (
              <div className="hidden md:flex items-center gap-2">
                <button
                  onClick={scrollLeft}
                  className="bg-primary/10 hover:bg-primary/20 text-primary rounded-lg p-2 transition-all duration-300 hover:scale-110"
                  aria-label="Anterior"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={scrollRight}
                  className="bg-primary/10 hover:bg-primary/20 text-primary rounded-lg p-2 transition-all duration-300 hover:scale-110"
                  aria-label="Próximo"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>

          <div className="relative">
            <div ref={scrollContainerRef} className="flex overflow-x-auto gap-6 pb-4 snap-x snap-mandatory scrollbar-hide">
              {/* Real Services */}
              {services.map((service: any, index: number) => {
                const info = getServiceInfo(service.service);
                const IconComponent = info.icon;

                const serviceImage = getServiceImage(service, info.image);

                return (
                  <Card key={index} className="group hover: transition-all duration-500 border-0  overflow-hidden flex flex-col flex-shrink-0 w-[280px] md:w-[320px] snap-start">
                    {/* Image Header */}
                    {serviceImage && (
                      <div className="relative aspect-[280/100] overflow-hidden">
                        <img
                          src={serviceImage}
                          alt={info.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                        {/* Icon Overlay */}
                        <div className="absolute top-4 right-4">
                          <div className={`w-12 h-12 ${info.color} rounded-xl flex items-center justify-center backdrop-blur-sm bg-white/10`}>
                            <IconComponent className="h-6 w-6 text-white" />
                          </div>
                        </div>

                        {/* Title Overlay */}
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="text-white font-bold text-lg leading-tight ">
                            {info.title}
                          </h3>
                        </div>
                      </div>
                    )}

                    {/* Content */}
                    <CardContent className="p-6 flex flex-col flex-grow">
                      <CardDescription className="text-muted-foreground mb-6 flex-grow">
                        {info.description}
                      </CardDescription>

                      {/* Action Button */}
                      <ServiceModal service={service} config={config} serviceImage={serviceImage}>
                        <Button
                          className="w-full text-white font-semibold py-3 rounded-xl  transition-all duration-300"
                          style={{
                            background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
                          }}
                        >
                          {getServiceButtonText(service.service)}
                        </Button>
                      </ServiceModal>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Placeholder Cards - Fill remaining slots up to 5 */}
              {services.length < 5 && Array.from({ length: 5 - services.length }).map((_, index) => (
                <Card key={`placeholder-${index}`} className="border-0  overflow-hidden flex flex-col flex-shrink-0 w-[280px] md:w-[320px] snap-start opacity-30">
                  {/* Placeholder Image Header */}
                  <div className="relative aspect-[280/100] overflow-hidden bg-muted/30">
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-muted/60 via-transparent to-transparent" />

                    {/* Icon Overlay */}
                    <div className="absolute top-4 right-4">
                      <div className="w-12 h-12 bg-muted/50 rounded-xl flex items-center justify-center">
                        <MessageCircle className="h-6 w-6 text-muted-foreground/40" />
                      </div>
                    </div>

                    {/* Title Overlay - Placeholder */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="h-5 bg-muted/60 rounded w-2/3" />
                    </div>
                  </div>

                  <div className="relative h-[120px] overflow-hidden bg-muted/40 rounded-xl border-2" />

                  <CardContent className="p-6 flex flex-col flex-grow">

                    {/* Description placeholder */}
                    <div className="mb-4 flex-grow space-y-2">
                      <div className="h-3 bg-muted/50 rounded" />
                      <div className="h-3 bg-muted/50 rounded w-5/6" />
                    </div>

                    {/* Button placeholder */}
                    <div className="h-11 bg-muted/40 rounded-xl border-2 border-dashed border-muted" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Precisa de Ajuda?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Nossa equipe especializada está pronta para atendê-lo. Entre em contato e descubra como podemos facilitar sua vida automotiva.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => openWhatsApp(store.whatsapp?.ios)}
                className="px-8"
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                Falar no WhatsApp
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => window.location.href = `tel:${store.phone?.formatted}`}
                className="px-8"
              >
                Ligar Agora
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer config={config} />
    </div>
  );
};

export default Services;